import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, Shield, Lock } from 'lucide-react';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { Separator } from '@/components/ui/separator';
import { nukeLocalSession } from '@/lib/supabaseClient';

import usePageTitle from '@/hooks/usePageTitle';
export default function Login() {
  usePageTitle('Sign in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const { signIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back. Take a breath, your numbers are here.');
      nav(loc.state?.from?.pathname || '/dashboard', { replace: true });
    } catch (err) {
      // Phase 40, if a user accidentally hits Login first when they
      // should be on Signup (no account exists yet), Supabase responds
      // with a generic "Invalid login credentials". For Google OAuth and
      // any future passwordless paths, the invite-only DB trigger may
      // surface its own message, translate that to actionable copy.
      const raw = (err?.message || '').toLowerCase();
      if (raw.includes('private beta') || raw.includes('waitlist')) {
        toast.error(
          '2120 is in private beta. Join the waitlist at 2120.money to receive an invitation.',
          { duration: 7000 },
        );
      } else {
        toast.error(err.message || 'Could not sign in.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid md:grid-cols-2">
      <div className="hidden md:flex bg-hero-soft items-center justify-center p-12">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-mark.png" alt="2120" className="h-10 w-10" />
            <img src="/brand/wordmark-green.png" alt="2120" className="h-7 w-auto dark:hidden" />
            <img src="/brand/wordmark-gold.png" alt="2120" className="h-7 w-auto hidden dark:block" />
          </div>
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Personal finance, reimagined
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">Financial clarity, without the complexity.</h1>
          <p className="text-muted-foreground leading-relaxed">2120 helps you move from financial stress to gentle awareness, not another budgeting app shouting at you.</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Private, encrypted, RLS-protected</li>
            <li className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Financial insights with context and clarity.</li>
            <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Your data, only yours</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md rounded-2xl border-border/70">
          <CardContent className="p-6 sm:p-8 space-y-5">
            {/* Mobile-only brand header (hidden on md+ since hero panel shows it) */}
            <div className="md:hidden flex flex-col items-center gap-2 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <img src="/brand/logo-mark.png" alt="2120" className="h-9 w-9" />
                <img src="/brand/wordmark-green.png" alt="2120" className="h-6 w-auto dark:hidden" />
                <img src="/brand/wordmark-gold.png" alt="2120" className="h-6 w-auto hidden dark:block" />
              </div>
              <div className="text-xs text-muted-foreground text-center">Financial Clarity For Modern Life.</div>
            </div>
            <div>
              <div className="font-display text-2xl font-semibold">Welcome back</div>
              <div className="text-sm text-muted-foreground mt-1">Sign in to your 2120 space.</div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input" className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password-input" className="rounded-xl" />
              </div>
              <Button
                type="submit"
                className="w-full rounded-xl"
                disabled={busy}
                data-testid="auth-submit-button"
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <Separator className="flex-1" /> or <Separator className="flex-1" />
            </div>
            <GoogleSignInButton label="Sign in with Google" testId="auth-google-button" />
            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground" data-testid="auth-forgot-password-link">Forgot password?</Link>
              <Link to="/signup" className="text-primary hover:underline" data-testid="auth-go-signup-link">Create an account</Link>
            </div>
            <div className="pt-2 border-t border-border/40 text-center">
              <button
                type="button"
                onClick={() => {
                  nukeLocalSession();
                  toast.success('Local session cleared. Try signing in again.');
                  // Hard reload to make sure the SDK starts from a clean slate.
                  setTimeout(() => window.location.reload(), 400);
                }}
                className="text-[11px] text-muted-foreground/70 hover:text-foreground underline-offset-2 hover:underline transition-colors"
                data-testid="auth-clear-local-session"
              >
                Stuck on &ldquo;Signing in…&rdquo;? Clear local session
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
